import BaseDataGrid from 'base-components/BaseDataGrid/BaseDataGrid';
import Filter from 'models/api/filter';
import Id from 'models/id';
import { useWorkplaceState } from 'providers/WorkplaceStateProvider';
import { PropsWithChildren } from 'react';
import { RowHeightArgs } from 'react-data-grid';
import './Workplace.scss';
import { ResponseData } from 'models/api/common';

type WorkplaceTableProps<T, FilterType> = PropsWithChildren<{
    lineHeight?: number | ((args: RowHeightArgs<T>) => number);
    rowClass?: (row: T) => string | undefined;
    clickable: boolean;
    showSummaryRows?: true;
    groupBy?: string[] | undefined;
    rowGrouper?: (
        rows: readonly T[],
        columnKey: string
    ) => Record<string, readonly T[]>;
    expandedGroupIds?: ReadonlySet<unknown>;
    onExpandedGroupIdsChange?: (expandedGroupIds: Set<unknown>) => void;
    tableData: ResponseData<T>,
    filter: FilterType,
    tableLoading: boolean,
}>;

export default function WorkplaceTable<
    T extends Id,
    ItemType extends T,
    FilterType extends Filter = Filter<T>,
    ChangeRequestType = ItemType,
    AddRequestType = ItemType,
>({
    lineHeight,
    rowClass,
    clickable,
    showSummaryRows,
    groupBy,
    rowGrouper,
    expandedGroupIds,
    onExpandedGroupIdsChange,
    children,
    tableData,
    filter,
    tableLoading,
}: WorkplaceTableProps<T, FilterType>) {
    const { setTableData, loading } =
        useWorkplaceState<T, ItemType, ChangeRequestType, AddRequestType>();

    return (
        <BaseDataGrid<T, T[]>
            reactDataGreedProps={{
                defaultColumnOptions: {
                    sortable: false,
                    resizable: true,
                },
                rowClass: rowClass,
                summaryRows: showSummaryRows && [tableData.records],
                groupBy: groupBy,
                rowGrouper: rowGrouper,
                expandedGroupIds: expandedGroupIds,
                onExpandedGroupIdsChange: onExpandedGroupIdsChange,
            }}
            rows={tableData.records}
            lineHeight={lineHeight}
            loading={loading || tableLoading}
            clickable={clickable}
            setTableData={setTableData}
            orderColumn={filter.orderColumn}
            orderDesc={filter.orderDesc}
        >
            {children}
        </BaseDataGrid>
    );
}
